import { IconDefinition, faArrowsRotate, faMicrophone, faVideo, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { TFunction } from 'i18next';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { ReactNode, useCallback, useRef } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Icon, Modal, ModalBody, ModalFooter, ModalHeader } from '@growtherapy/sprout-ui';

import { MediaRecoveryEvent, RecoveryMediaType, mediaRecoveryEventAtom } from '../../video-sdk';

export type MediaRecoveryModalProps = {
  isOpen: boolean;
  title: ReactNode;
  description: ReactNode;
  confirmText: string;
  onConfirm: () => void;
  onClose: () => void;
  icon?: IconDefinition;
};

function MediaRecoveryModal(props: MediaRecoveryModalProps) {
  const { isOpen, title, description, confirmText, onConfirm, onClose, icon } = props;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='sm:mt-[10%]'>
      <ModalHeader>
        <span className='flex items-center gap-2'>
          {icon && <Icon className='shrink-0' icon={icon} />}
          <span>{title}</span>
        </span>
      </ModalHeader>
      <ModalBody>
        <Body.Default>{description}</Body.Default>
      </ModalBody>
      <ModalFooter
        actions={[
          {
            key: 'media-recovery-confirm',
            use: 'primary',
            'aria-label': confirmText,
            onClick: onConfirm,
            actionContent: confirmText,
          },
        ]}
      />
    </Modal>
  );
}

type RecoveryContent = {
  icon: IconDefinition;
  title: string;
  description: ReactNode;
  confirmText: string;
  onConfirm: () => void;
};

// localized noun for the failed media
function getMediaNoun(t: TFunction, mediaType: RecoveryMediaType): string {
  switch (mediaType) {
    case 'audio':
      return t('telehealth-core:mediaRecovery.media.audio');
    case 'video':
      return t('telehealth-core:mediaRecovery.media.video');
    case 'sharing':
      return t('telehealth-core:mediaRecovery.media.screenShare');
    default: {
      const _exhaustive: never = mediaType;
      return _exhaustive;
    }
  }
}

// the close handler doubles as the gesture that resumes media
function getRecoveryContent(event: MediaRecoveryEvent, t: TFunction, onClose: () => void): RecoveryContent {
  const { kind, type } = event;
  switch (kind) {
    case 'interrupted':
      return {
        icon: type === 'video' ? faVideo : faMicrophone,
        title: t('telehealth-core:mediaRecovery.interrupted.title', {
          media: getMediaNoun(t, type),
        }),
        description: t('telehealth-core:mediaRecovery.interrupted.description'),
        confirmText: t('telehealth-core:mediaRecovery.interrupted.action'),
        onConfirm: onClose,
      };
    case 'blocked':
      if (type === 'video') {
        return {
          icon: faVideo,
          title: t('telehealth-core:mediaRecovery.blocked.videoTitle'),
          description: t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraSelectSettingsIcon'),
          confirmText: t('telehealth-core:core.done'),
          onConfirm: onClose,
        };
      }
      return {
        icon: faMicrophone,
        title: t('telehealth-core:mediaRecovery.blocked.audioTitle'),
        description: t('telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicSelectSettingsIcon'),
        confirmText: t('telehealth-core:core.done'),
        onConfirm: onClose,
      };
    case 'failed':
      return {
        icon: faArrowsRotate,
        title: t('telehealth-core:errorsCore.somethingWentWrong'),
        description: t('telehealth-core:mediaRecovery.failed.description', {
          media: getMediaNoun(t, type),
        }),
        confirmText: t('telehealth-core:mediaRecovery.failed.action'),
        onConfirm: () => window.location.reload(),
      };
    case 'corrupted':
      return {
        icon: faWarning,
        title: t('telehealth-core:errorsCore.somethingWentWrong'),
        description: t('telehealth-core:mediaRecovery.corrupted.description', {
          media: getMediaNoun(t, type),
        }),
        confirmText: t('telehealth-core:core.done'),
        onConfirm: onClose,
      };
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

export function MediaRecoveryModalWrapper() {
  const t = useTranslate();
  const mediaRecoveryEvent = useAtomValue(mediaRecoveryEventAtom);
  const onClose = useAtomCallback(useCallback((_get, set) => set(mediaRecoveryEventAtom, null), []));

  // retain the last recovery event so the modal can animate closed after the atom clears
  const lastRecoveryEvent = useRef<MediaRecoveryEvent | null>(mediaRecoveryEvent);
  if (mediaRecoveryEvent) lastRecoveryEvent.current = mediaRecoveryEvent;
  const active = mediaRecoveryEvent ?? lastRecoveryEvent.current;

  if (!active) return null;

  const { icon, title, description, confirmText, onConfirm } = getRecoveryContent(active, t, onClose);

  return (
    <MediaRecoveryModal
      isOpen={mediaRecoveryEvent !== null}
      icon={icon}
      title={title}
      description={description}
      confirmText={confirmText}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}
